import { useState } from "react";
import { toast } from "react-toastify";
import { useAdminAccountControllerUpdateStatus } from "service";
import { AdminAccountResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "@/query-client";

export interface UseMemberAccessToggle {
  pendingMember: AdminAccountResDto | null;
  isSubmitting: boolean;
  requestToggle: (member: AdminAccountResDto) => void;
  confirmToggle: () => void;
  cancelToggle: () => void;
}

const useMemberAccessToggle = (): UseMemberAccessToggle => {
  const [pendingMember, setPendingMember] = useState<AdminAccountResDto | null>(
    null,
  );
  // IDs with a toggle currently in flight. `isLoading` on the mutation itself
  // is shared across every member, so it can't tell "a request is running"
  // from "a request is running for the member whose dialog happens to be
  // open right now" — this does, and also lets the dialog auto-close only
  // when its own member's request is the one that resolved.
  const [submittingMemberIds, setSubmittingMemberIds] = useState<Set<string>>(
    new Set(),
  );

  const { mutate } = useAdminAccountControllerUpdateStatus({
    onSuccess: (response, variables) => {
      toast.success(
        response.data?.isActive
          ? "Access Successfully Restored."
          : "Access Successfully Removed.",
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/accounts",
          operationId: "adminAccountControllerGetAll",
          variables: {},
        }),
      );
      const toggledId = variables.pathParams.id;
      setPendingMember((current) =>
        current?.id === toggledId ? null : current,
      );
      setSubmittingMemberIds((current) => {
        const next = new Set(current);
        next.delete(toggledId);
        return next;
      });
    },
    onError: (_error, variables) => {
      toast.error("Couldn't update member access. Please try again.");
      setSubmittingMemberIds((current) => {
        const next = new Set(current);
        next.delete(variables.pathParams.id);
        return next;
      });
    },
  });

  const requestToggle = (member: AdminAccountResDto) =>
    setPendingMember(member);
  const cancelToggle = () => setPendingMember(null);
  const confirmToggle = () => {
    if (!pendingMember) return;
    setSubmittingMemberIds((current) => new Set(current).add(pendingMember.id));
    mutate({
      pathParams: { id: pendingMember.id },
      body: { isActive: !pendingMember.isActive },
    });
  };

  return {
    pendingMember,
    isSubmitting: pendingMember
      ? submittingMemberIds.has(pendingMember.id)
      : false,
    requestToggle,
    confirmToggle,
    cancelToggle,
  };
};

export default useMemberAccessToggle;
